import { create, registerPlugin } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import { FF_utils } from './lib/utils.js';

interface ContactResponse {
	ticket?: unknown;
	ra?: string;
}

class ffContact {
	submissionId: string;
	pond: unknown;

	state = {
		tried: false,
		working: false,
		valid: false,
		error: null as string | null,
		success: null as boolean | null,
		messages: [] as string[],
		topic: '',
	};

	DOM: {
		form: HTMLFormElement;
		submit: HTMLButtonElement;
		pond: HTMLInputElement;
		systems: NodeListOf<HTMLInputElement>;
		errorMessage: HTMLElement;
		topic: HTMLSelectElement | null;
		appCol: HTMLElement | null;
		appVersion: HTMLInputElement | null;
		spinner: HTMLElement;
	} = {} as ffContact['DOM'];

	constructor() {
		this.setSubmissionId();
		this.cache();
		if (!this.DOM.form) return;
		this.events();
		this.state.topic = this.DOM.topic ? this.DOM.topic.value : '';
		this.render();
		this.initUpload();
	}

	setSubmissionId() {
		const ff_utils = new FF_utils();
		this.submissionId = ff_utils.cuid();
	}

	cache() {
		const form = document.querySelector('.js-contactForm');
		if (!form) return;
		this.DOM = {
			form: form as HTMLFormElement,
			submit: form.querySelector('.js-submitButton') as HTMLButtonElement,
			pond: form.querySelector('input[name="documents[]"]') as HTMLInputElement,
			systems: form.querySelectorAll('[name=system]'),
			errorMessage: form.querySelector('.js-errorMessage') as HTMLElement,
			topic: form.querySelector('[name=topic]') as HTMLSelectElement | null,
			appCol: form.querySelector('.js-appColumn') as HTMLElement | null,
			appVersion: form.querySelector('input[name=app]') as HTMLInputElement | null,
			spinner: form.querySelector('.js-spinner') as HTMLElement,
		};
	}

	events() {
		this.DOM.form.addEventListener('submit', this.submit.bind(this));
		if (this.DOM.topic) {
			this.DOM.topic.addEventListener('change', this.topicChange.bind(this));
		}

		if (this.DOM.topic) {
			this.DOM.topic.addEventListener('change', this.reValidate.bind(this));
		}
		if (this.DOM.appVersion) {
			this.DOM.appVersion.addEventListener('change', this.reValidate.bind(this));
		}
		for (var i = this.DOM.systems.length - 1; i >= 0; i--) {
			this.DOM.systems[i].addEventListener('change', this.reValidate.bind(this));
		}
	}

	topicChange() {
		this.state.topic = this.DOM.topic?.value || '';
		this.reValidate();
		this.render();
	}

	submit(e: SubmitEvent) {
		this.state.tried = true;
		e.preventDefault();

		if (this.state.success || this.state.working) return;

		var valid = this.validate();
		if (!valid) {
			this.render();
			return;
		}

		this.state.working = true;
		this.render();
		const formData = new FormData(this.DOM.form);
		formData.append('action', 'contact_submit');
		formData.append('id', this.submissionId);

		if (typeof grecaptcha === 'undefined') {
			this.showErrorMessage(
				'Unable to verify your submission. Please disable ad blockers and try again.'
			);
			this.state.working = false;
			this.render();
			return;
		}

		grecaptcha.ready(() => {
			grecaptcha
				.execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
				.then((token: string) => {
					formData.append('g-recaptcha-response', token);
					return fetch(ajaxurl, {
						method: 'POST',
						body: formData,
					}).then(this.formResponse.bind(this));
				})
				.catch((error: unknown) => {
					this.showErrorMessage('Unable to verify your submission. Please try again.');
					this.state.working = false;
					this.render();
					console.log('Recaptcha error', error);
				});
		});
	}

	validate() {
		const checkedSystem = this.DOM.form.querySelector(
			'[name=system]:checked'
		) as HTMLInputElement | null;
		const anySystemInput = this.DOM.form.querySelector('[name=system]') as HTMLInputElement | null;
		const systemValue = checkedSystem
			? checkedSystem.value
			: anySystemInput
				? anySystemInput.value
				: '';
		const topic = this.DOM.topic ? this.DOM.topic.value : '';
		let valid = true;
		let messages: string[] = [];
		if (!systemValue) {
			messages.push('Please select a system.');
			valid = false;
		}
		if (topic === '' || topic === 'default') {
			messages.push('Please select a topic.');
			valid = false;
		} else if (
			topic.indexOf('Technical Support (Software/App)') > -1 &&
			this.DOM.appVersion?.value === ''
		) {
			messages.push('Please provide app details.');
			valid = false;
		}
		this.state.messages = messages;
		this.state.valid = valid;
		return valid;
	}

	reValidate() {
		if (!this.state.tried) return;
		this.validate();
		this.render();
	}

	render() {
		if ((!this.state.valid && this.state.tried) || this.state.working || this.state.success) {
			this.DOM.submit.disabled = true;
		} else {
			this.DOM.submit.disabled = false;
		}

		if (this.DOM.appCol) {
			if (this.state.topic && this.state.topic.indexOf('Technical Support (Software/App)') > -1) {
				this.DOM.appCol.classList.add('js-active');
			} else {
				this.DOM.appCol.classList.remove('js-active');
			}
		}

		if (!this.state.valid && this.state.tried && this.state.messages.length > 0) {
			const message = `
			<div class="messageHeader"><h2 class="messageHl">Error</h2></div>
			<div class="messageBody">
			<p>There&rsquo;s a problem with your submission:</p>
			<ul>
			${this.state.messages.map((el) => `<li>${el}</li>`).join('')}
			</ul>
			</div>
			`;
			this.DOM.errorMessage.innerHTML = message;
			this.DOM.errorMessage.classList.remove('js-hide');
		} else {
			this.DOM.errorMessage.classList.add('js-hide');
		}

		if (this.state.working) {
			this.DOM.spinner.classList.remove('js-hide');
		} else {
			this.DOM.spinner.classList.add('js-hide');
		}
	}

	async formResponse(res: Response) {
		this.state.working = false;
		let data: ContactResponse | undefined;
		try {
			data = await res.json();
		} catch {
			this.showErrorMessage(String(res));
			this.render();
			return;
		}
		if (data?.ticket) {
			this.showSuccessMessage(data.ra || '');
			this.state.success = true;
		} else {
			console.log(data);
			this.showErrorMessage(`Request could not be processed.`);
		}
		this.render();
	}

	initUpload() {
		registerPlugin(FilePondPluginFileValidateSize);
		registerPlugin(FilePondPluginFileValidateType);

		const pondConfig = {
			credits: false as const,
			maxFiles: 10,
			acceptedFileTypes: ['image/*', 'application/pdf'],
			maxFileSize: '5MB',
			allowMultiple: true,
			server: {
				process: {
					url: ajaxurl,
					onload: (response: string) => {
						const parsed = JSON.parse(response) as { key: string };
						return parsed.key;
					},
					ondata: (formData: FormData) => {
						formData.append('action', 'contact_s3_upload');
						formData.append('id', this.submissionId);
						return formData;
					},
				},
			},
		};
		this.pond = create(this.DOM.pond, pondConfig);
	}

	showSuccessMessage(_ra: string) {
		const section = document.createElement('div');
		section.classList.add('message', 'success');
		const html = `
<div class="messageHeader">
  <h2 class="messageHl">Success!</h2>
</div>
<div class="messageBody">
	<p>We received your request. Our support team will review your submission and be in touch&nbsp;shortly.</p>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}

	showErrorMessage(message: string) {
		const section = document.createElement('div');
		section.classList.add('message', 'error');
		const html = `
<div class="messageHeader">
  <h2 class="messageHl">Error</h2>
</div>
<div class="messageBody">
  <div class="formRow">
    <div class="formCol">
      <p>Uh oh. There was an issue with the form submission:</p>
      ${message ? `<pre>${message}</pre>` : ''}
      <p>Please <a href="mailto:support@freeflysystems.com">email support</a> for help.</p>
    </div>
  </div>
</div>
`;
		section.innerHTML = html;
		this.DOM.form.appendChild(section);
	}
}

new ffContact();
